import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import { CallProvider } from "@/context/CallContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import BannedScreen from "@/components/BannedScreen";
import { useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import ProfilePage from "@/pages/ProfilePage";
import ProfileViewPage from "@/pages/ProfileViewPage";
import DiscoverPage from "@/pages/DiscoverPage";
import FriendsPage from "@/pages/FriendsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import MessagesPage from "@/pages/MessagesPage";
import PublicChatPage from "@/pages/PublicChatPage";
import PublicChatDesigner from "@/pages/PublicChatDesigner";
import ComingSoon from "@/pages/ComingSoon";
import SettingsPage from "@/pages/SettingsPage";
import WalletStorePage from "@/pages/WalletStorePage";
import AdminPage from "@/pages/AdminPage";
import AdminRoute from "@/components/AdminRoute";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { Video } from "lucide-react";
import RandomVideoPage from "@/pages/RandomVideoPage";

function AppRouter() {
  const { ipBan } = useAuth();
  const location = useLocation();

  // Every page starts at the top of the document. This does not reset
  // independent scroll areas such as the message thread or public chat feed.
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    const markGalleryHistory = (event) => {
      const button = event.target.closest?.("button");
      if (!button || !button.querySelector?.('img[alt="Gallery"]')) return;
      if (window.history.state?.__yaprioGallery) return;
      window.history.pushState({ ...(window.history.state || {}), __yaprioGallery: true }, "", window.location.href);
    };

    const handlePopState = () => {
      const closeGallery = document.querySelector('[aria-label="Close gallery"]');
      if (closeGallery) closeGallery.click();
    };

    const closeGalleryWithHistory = (event) => {
      if (event.key === "Escape" && document.querySelector('[aria-label="Close gallery"]')) {
        if (window.history.state?.__yaprioGallery) window.history.back();
      }
    };

    document.addEventListener("click", markGalleryHistory, true);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("keydown", closeGalleryWithHistory);
    return () => {
      document.removeEventListener("click", markGalleryHistory, true);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("keydown", closeGalleryWithHistory);
    };
  }, []);

  if (ipBan) return <BannedScreen ban={ipBan} ipBan />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
      <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/random" element={<ProtectedRoute><RandomVideoPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/public-chat" element={<ProtectedRoute><PublicChatPage /></ProtectedRoute>} />
      <Route path="/design/public-chat" element={<ProtectedRoute><PublicChatDesigner /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminPage /></AdminRoute></ProtectedRoute>} />
      <Route path="/u/:username" element={<ProtectedRoute><ProfileViewPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletStorePage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <RealtimeProvider>
            <CallProvider>
              <BrowserRouter>
                <AppRouter />
                <Toaster position="top-center" richColors />
              </BrowserRouter>
            </CallProvider>
          </RealtimeProvider>
        </AuthProvider>
      </ThemeProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
